// Carrier edit row — extracted from LogisticsCarriersList.tsx as the
// D3j prep split. Pure presentation component: takes the carrier + the
// panel hook (for the edit draft state and submitEdit/cancelEdit). All
// state lives upstream on `m.edit` / `m.setEdit`.
import {
  CARRIER_KIND_LABELS,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsCarriersShared";
import type { useLogisticsCarriersPanel } from "./useLogisticsCarriersPanel";
import type { LogiCarrier, LogiCarrierKind } from "@/lib/logistics";

type Props = {
  carrier: LogiCarrier;
  m: ReturnType<typeof useLogisticsCarriersPanel>;
};

export function LogisticsCarriersEditRow({ carrier, m }: Props) {
  return (
    <li className="py-3">
      <div className="space-y-2">
        <div className="text-xs text-gray-500 dark:text-zinc-500">Editing carrier</div>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Name
          <input
            className={`${inputClass} mt-1`}
            value={m.edit.name}
            onChange={(e) => m.setEdit({ ...m.edit, name: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Kind
          <select
            className={`${inputClass} mt-1`}
            value={m.edit.carrier_kind}
            onChange={(e) =>
              m.setEdit({ ...m.edit, carrier_kind: e.target.value as LogiCarrierKind })
            }
          >
            {m.kindOptions.map((k) => (
              <option key={k} value={k}>
                {CARRIER_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Contact name
            <input
              className={`${inputClass} mt-1`}
              value={m.edit.contact_name}
              onChange={(e) => m.setEdit({ ...m.edit, contact_name: e.target.value })}
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Contact email
            <input
              className={`${inputClass} mt-1`}
              value={m.edit.contact_email}
              onChange={(e) => m.setEdit({ ...m.edit, contact_email: e.target.value })}
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Contact phone
            <input
              className={`${inputClass} mt-1`}
              value={m.edit.contact_phone}
              onChange={(e) => m.setEdit({ ...m.edit, contact_phone: e.target.value })}
            />
          </label>
        </div>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Coverage areas (comma-separated)
          <input
            className={`${inputClass} mt-1`}
            value={m.edit.coverage_areas_text}
            onChange={(e) => m.setEdit({ ...m.edit, coverage_areas_text: e.target.value })}
          />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Capacity (linear ft)
            <input
              className={`${inputClass} mt-1`}
              value={m.edit.capacity_linear_feet_text}
              onChange={(e) =>
                m.setEdit({ ...m.edit, capacity_linear_feet_text: e.target.value })
              }
              inputMode="numeric"
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Insurance expires
            <input
              type="date"
              className={`${inputClass} mt-1`}
              value={m.edit.insurance_expires_at}
              onChange={(e) => m.setEdit({ ...m.edit, insurance_expires_at: e.target.value })}
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Contract expires
            <input
              type="date"
              className={`${inputClass} mt-1`}
              value={m.edit.contract_expires_at}
              onChange={(e) => m.setEdit({ ...m.edit, contract_expires_at: e.target.value })}
            />
          </label>
        </div>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Notes
          <input
            className={`${inputClass} mt-1`}
            value={m.edit.notes}
            onChange={(e) => m.setEdit({ ...m.edit, notes: e.target.value })}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={primaryBtnClass}
            onClick={() => void m.submitEdit()}
            disabled={m.actionBusy}
          >
            {m.actionBusy ? "Saving…" : "Save"}
          </button>
          <button type="button" className={ghostBtnClass} onClick={m.cancelEdit}>
            Cancel
          </button>
          <span className="text-xs text-gray-400 dark:text-zinc-500 self-center">
            Editing <code>{carrier.id.slice(0, 8)}</code>
          </span>
        </div>
      </div>
    </li>
  );
}
